import React from 'react';
import {
  ShieldCheck, Lock, BadgeCheck, AlertTriangle, Phone, Mail, MapPin,
  FileCheck, Eye, UserCheck, ArrowRight, Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Seo } from '../components/seo/Seo';
import { organizationSchema, breadcrumbSchema } from '../lib/seoSchemas';
import { Footer } from '../components/brand/Footer';
import { TrustBadge } from '../components/brand/TrustBadge';
import { MarketplaceDisclaimer } from '../components/brand/MarketplaceDisclaimer';

const VETTING_SYSTEM = [
  {
    icon: UserCheck,
    title: 'Identity verification',
    summary: 'Every clinician is exactly who they say they are.',
    detail: 'A government-issued photo ID is matched to a live selfie using biometric face-match technology. No anonymous profiles — ever.',
  },
  {
    icon: FileCheck,
    title: 'License & credential validation',
    summary: 'Active, unrestricted licensure — verified at the source.',
    detail: 'Primary-source verification against state licensing boards and the national NPI registry, plus proof of malpractice insurance and any specialty certifications a clinician lists on their profile.',
  },
  {
    icon: ShieldCheck,
    title: 'Multi-state background screening',
    summary: 'A clean record, checked nationwide.',
    detail: 'National and county criminal database searches, the national sex-offender registry, and OIG/GSA healthcare-sanctions exclusion screening — completed before a clinician is ever approved.',
  },
  {
    icon: BadgeCheck,
    title: 'References & verified work history',
    summary: 'A real track record of safe, quality care.',
    detail: 'We confirm professional work history and contact references, so the experience and specialties shown on every profile are ones you can trust.',
  },
  {
    icon: Eye,
    title: 'Continuous monitoring',
    summary: 'Vetting never stops after approval.',
    detail: 'Real-time license-status monitoring and periodic re-screening. Any revocation, new sanction, or safety complaint is flagged and reviewed by our licensed Trust & Safety team within hours.',
  },
];

const SAFETY_FEATURES = [
  { icon: Lock, title: 'End-to-end encrypted messaging', desc: 'No third party can read your conversations with clinicians.' },
  { icon: ShieldCheck, title: 'Verified-settlement payments', desc: 'Funds settle to the clinician only after the visit is verified.' },
  { icon: MapPin, title: 'Visit check-ins', desc: 'EVV-backed visit start/stop records for every home visit.' },
  { icon: Eye, title: 'Visit oversight', desc: 'Optional geo-tracking and check-in/check-out timestamps.' },
  { icon: AlertTriangle, title: 'Incident reporting', desc: 'Confidential reports reviewed by a licensed safety team.' },
  { icon: Sparkles, title: 'AI fraud detection', desc: 'Anomaly scoring on pricing, behavior, and identity signals.' },
];

export default function TrustSafetyPage() {
  return (
    <div className="min-h-screen bg-cn-bg" data-testid="trust-safety-page">
      <Seo
        title="Trust & Safety | Verified, Background-Checked Clinicians | CaraLoom"
        description="How CaraLoom keeps care safe: identity verification, license validation, background checks, continuous monitoring, and HIPAA-ready, encrypted communication."
        path="/trust-safety"
        keywords={['background-checked caregivers', 'verified clinicians', 'home care safety']}
        schema={[organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Trust & Safety', path: '/trust-safety' }])]}
      />
      <PublicNavbar />

      {/* Hero */}
      <section className="border-b border-cn-border bg-white dark:bg-cn-ink-900">
        <div className="cn-container py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="max-w-3xl lg:col-span-7">
            <div className="cn-eyebrow mb-3">Trust & safety</div>
            <h1 className="cn-display text-4xl md:text-6xl text-cn-text leading-tight">
              Safety, engineered into<br />
              <span className="italic font-medium text-cn-primary">every visit.</span>
            </h1>
            <p className="mt-6 text-cn-text-muted text-lg leading-relaxed">
              CaraLoom is built around one promise: care that comes home to your family
              should never compromise on safety. Here&apos;s exactly how we protect everyone
              on the platform.
            </p>
            <div className="mt-7 flex flex-wrap gap-2">
              <TrustBadge variant="background-checked" />
              <TrustBadge variant="license" />
              <TrustBadge variant="encrypted" label="HIPAA-ready" />
              <TrustBadge variant="verified" label="ID-verified" />
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden cn-card aspect-[4/5]">
              <img
                src="https://images.pexels.com/photos/36764709/pexels-photo-36764709.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                alt="Caregiver and patient holding hands"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-cn-text/80 to-transparent">
                <span className="cn-trust-badge !bg-white/95 dark:!bg-cn-ink-700">
                  <ShieldCheck className="w-3.5 h-3.5" /> Every visit, protected
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How safety is enforced — process guarantees, not vanity numbers */}
      <section className="border-b border-cn-border bg-cn-bone-100 dark:bg-cn-ink-900" data-testid="trust-process-strip">
        <div className="cn-container py-12 grid grid-cols-2 md:grid-cols-4 gap-6">
          <ProcessPoint title="Identity-verified" desc="Government ID + biometric face match" />
          <ProcessPoint title="License-verified" desc="Primary-source, continuously monitored" />
          <ProcessPoint title="Background-screened" desc="National, county & sanctions checks" />
          <ProcessPoint title="Verified-settlement" desc="Funds settle only after the visit is verified" />
        </div>
      </section>

      {/* 5-point vetting system */}
      <section className="py-20 md:py-24" data-testid="vetting-system">
        <div className="cn-container">
          <div className="max-w-2xl mb-12">
            <div className="cn-eyebrow mb-3">Verification process</div>
            <h2 className="cn-display text-4xl">Our 5-point vetting system.</h2>
            <p className="mt-3 text-cn-text-muted">
              Before any clinician can take a single visit on CaraLoom, they must clear five independent
              checks — and we keep checking for as long as they remain on the platform. Here&apos;s exactly
              what that means.
            </p>
          </div>
          <ol className="space-y-4">
            {VETTING_SYSTEM.map((s, i) => (
              <li
                key={s.title}
                className="cn-card cn-card-hover p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-5"
                data-testid={`vetting-point-${i + 1}`}
              >
                <div className="flex items-center gap-4 md:w-80 flex-shrink-0">
                  <div className="font-mono text-sm text-cn-text-muted w-6 flex-shrink-0">0{i + 1}</div>
                  <div className="w-12 h-12 rounded-2xl bg-cn-sage-100 flex items-center justify-center flex-shrink-0">
                    <s.icon className="w-5 h-5 text-cn-primary" />
                  </div>
                  <h3 className="font-display font-semibold text-lg text-cn-text leading-tight">{s.title}</h3>
                </div>
                <div className="md:flex-1 md:border-l md:border-cn-border md:pl-6">
                  <p className="font-semibold text-cn-text text-sm">{s.summary}</p>
                  <p className="text-sm text-cn-text-muted mt-1 leading-relaxed">{s.detail}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="mt-8 text-sm text-cn-text-muted max-w-2xl">
            Clinicians who pass every step earn the{' '}
            <span className="inline-flex items-center gap-1 font-semibold text-cn-primary"><BadgeCheck className="w-4 h-4" /> Verified</span>{' '}
            badge you see on their profiles. Vetting and screening are performed with accredited third-party providers.
          </p>
        </div>
      </section>

      {/* Safety features */}
      <section className="py-20 md:py-24 bg-cn-bone-100 dark:bg-cn-ink-900 border-y border-cn-border">
        <div className="cn-container">
          <div className="max-w-2xl mb-12">
            <div className="cn-eyebrow mb-3">Safety on every visit</div>
            <h2 className="cn-display text-4xl">Protection built into every interaction.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SAFETY_FEATURES.map((s) => (
              <div key={s.title} className="cn-card p-6">
                <div className="w-10 h-10 rounded-xl bg-cn-terracotta-50 border border-cn-terracotta-200 flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5 text-cn-terracotta-500" />
                </div>
                <h3 className="font-display font-semibold">{s.title}</h3>
                <p className="text-sm text-cn-text-muted mt-1 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Report an incident CTA */}
      <section className="py-20 md:py-24">
        <div className="cn-container">
          <div className="cn-card p-10 md:p-14 !bg-cn-primary text-white relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full bg-cn-terracotta-400/30 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="cn-eyebrow !text-white/70 mb-3">Report an incident</div>
                <h2 className="cn-display text-3xl md:text-4xl !text-white">If something feels wrong, tell us immediately.</h2>
                <p className="mt-4 text-white/80">Safety reports are reviewed by our licensed Trust &amp; Safety team and prioritized ahead of every other request.</p>
              </div>
              <div className="flex flex-col gap-3 md:items-end">
                <Link to="/contact" className="cn-btn cn-btn-accent text-base" data-testid="ts-report-cta">
                  Report a safety concern <ArrowRight className="w-4 h-4" />
                </Link>
                <a href="mailto:care@caraloom.com" className="text-white/80 hover:text-white inline-flex items-center gap-2 text-sm" data-testid="ts-safety-email">
                  <Mail className="w-4 h-4" /> care@caraloom.com
                </a>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <MarketplaceDisclaimer variant="banner" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ProcessPoint({ title, desc }) {
  return (
    <div>
      <div className="font-display text-lg md:text-xl font-bold text-cn-text inline-flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-cn-success flex-shrink-0" /> {title}
      </div>
      <div className="text-xs text-cn-text-muted uppercase tracking-wider mt-1">{desc}</div>
    </div>
  );
}
